import React, { createContext, useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react';
import localforage from 'localforage';

const settingsStore = localforage.createInstance({ name: 'settings' });

export const Context = createContext({});

export const Provider = (props) => {
  // Initial values are obtained from the props
  const { children } = props;

  // Use State to keep the values
  const [searchValue, setSearchValue] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSort, setSelectedSort] = useState(null);
  const [showFavourites, setShowFavourites] = useState(false);
  const [customerCategory, setCustomerCategory] = useState(false);
  const [showInAssortment, setShowInAssortment] = useState(true);
  const [showDiscounted, setShowDiscounted] = useState(false);
  const [showColored, setShowColored] = useState(false);
  const [showUlistan, setShowUlistan] = useState(false);
  const [orderByFrequency, setOrderByFrequency] = useState(true);
  const [resultCount, setResultCount] = useState(0);
  const [hasQty, setHasQty] = useState(false);
  const [isPackageTypingEnabled, setIsPackageTypingEnabled] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showPdvPrice, setShowPdvPrice] = useState(false);

  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    settingsStore.getItem('selectedSort').then((saved) => {
      if (saved) setSelectedSort(saved);
    }).catch(() => {});
  }, []);

  const persistSort = useCallback((sort) => {
    setSelectedSort(sort);
    if (sort) {
      settingsStore.setItem('selectedSort', sort).catch(() => {});
    } else {
      settingsStore.removeItem('selectedSort').catch(() => {});
    }
  }, []);

  const isFiltered = useMemo(() => {
    return selectedBrand ||
      selectedCategory ||
      showFavourites ||
      showDiscounted ||
      customerCategory ||
      showColored ||
      hasQty ||
      showUlistan ||
      !showInAssortment ||
      selectedSupplier;
  }, [selectedBrand, selectedCategory, showFavourites, showDiscounted, customerCategory, showColored, hasQty, showUlistan, showInAssortment, selectedSupplier]);

  const clearFilters = useCallback(() => {
    setSelectedBrand(null);
    setSelectedCategory(null);
    setShowFavourites(false);
    setShowDiscounted(false);
    setShowColored(false);
    setCustomerCategory(false);
    setHasQty(false);
    setShowUlistan(false);
    setShowInAssortment(true);
    setSelectedSupplier(null);
  }, []);

  const togglePackageTyping = useCallback(() => {
    setIsPackageTypingEnabled(prev => !prev);
  }, []);

  // Memoize the context object to prevent unnecessary re-renders
  const productContext = useMemo(() => ({
    searchValue,
    setSearchValue,
    selectedBrand,
    setSelectedBrand,
    selectedCategory,
    setSelectedCategory,
    listRef,
    showFavourites,
    setShowFavourites,
    showDiscounted,
    setShowDiscounted,
    showColored,
    setShowColored,
    resultCount,
    setResultCount,
    loading,
    setLoading,
    isFiltered,
    clearFilters,
    orderByFrequency,
    setOrderByFrequency,
    customerCategory,
    setCustomerCategory,
    showInAssortment,
    setShowInAssortment,
    hasQty,
    setHasQty,
    showUlistan,
    setShowUlistan,
    setSelectedSort: persistSort,
    selectedSort,
    isPackageTypingEnabled,
    setIsPackageTypingEnabled,
    togglePackageTyping,
    selectedSupplier,
    setSelectedSupplier,
    showPdvPrice,
    setShowPdvPrice,
  }), [
    searchValue,
    selectedBrand,
    selectedCategory,
    showFavourites,
    showDiscounted,
    showColored,
    resultCount,
    loading,
    isFiltered,
    orderByFrequency,
    customerCategory,
    showInAssortment,
    hasQty,
    showUlistan,
    selectedSort,
    isPackageTypingEnabled,
    clearFilters,
    togglePackageTyping,
    selectedSupplier,
    showPdvPrice,
  ]);

  // pass the value in provider and return
  return <Context.Provider value={productContext}>{children}</Context.Provider>;
};

export const { Consumer } = Context;

export const useProductContext = () => useContext(Context);
